import type { HandoffPlatform } from '@usacsmideo/types';
import { detectPlatform } from '../../utilities/detectPlatform';

/**
 * "View in larger map" external handoff for the Session Location Map
 * (CU-86ajd3yt0, epic CU-86ajd302x). Pure, rendering-free URL construction so
 * the per-platform branching is unit-testable without a DOM.
 *
 * The handoff is the SINGLE third-party PHI-boundary crossing in the whole
 * feature: the scanner's coordinate leaves MIDEO to Apple Maps / OpenStreetMap
 * tied to the viewer. That is why it is consent-gated + audited elsewhere; this
 * module only builds the destination URL.
 *
 * VIEW-ONLY, not directions. A "Get Directions" tier was considered and dropped
 * (CU-86ajd3yt0): routing to an approximate, *scan-time* coordinate overpromises
 * both precision and currency, and the only provider that routes cleanly from a
 * destination-only link (Google) does so by ingesting the patient coordinate
 * into its ad graph — the exact third-party disclosure D self-hosts tiles to
 * avoid. (A future viewer-initiated routing path that uses the VIEWER's own
 * consented location as the origin is tracked separately.)
 *
 * Honesty constraint: the third-party map drops a single crisp pin and CANNOT
 * render our accuracy circle, so the pin must not read as a confirmed address.
 * Where the platform lets a URL title the pin (Android `geo:` `q=...()`), we
 * carry an "approximate" label. Where it does not (iOS `ll=`, desktop OSM
 * `mlat/mlon`), the consent dialog + caption copy are the honesty layer.
 */

/** Minimal coordinate shape this module needs (subset of the wire `location`). */
export interface HandoffLocation {
  lat: number;
  lng: number;
  /** Accuracy radius in meters, as reported by the browser. */
  accuracy: number;
}

/**
 * The audit platform for the consent ack (`HandoffPlatform` = 'ios' | 'android'
 * | 'web'). `detectPlatform()` returns 'desktop'; we normalize that to 'web'
 * (an iPad reporting a desktop UA also lands here — desktop OSM works there).
 */
export function handoffPlatform(): HandoffPlatform {
  const p = detectPlatform();
  return p === 'desktop' ? 'web' : p;
}

/** Round accuracy to a whole meter for the human-facing pin label. */
function accuracyLabel(accuracy: number): string {
  return `Approx. scan location ±${Math.round(accuracy)}m`;
}

/**
 * Accuracy (meters) at or below which offering "Get directions" is meaningful.
 * Routing to a coordinate fuzzier than this points turn-by-turn at a blob, so we
 * suppress the directions button above it (the plain "View in larger map" stays).
 * This is the honest half of the original two-tier idea — correctly scoped to
 * ROUTING only (viewing a fuzzy pin is fine; routing to it is not). Named +
 * exported as the single tunable point; revisit once real staging `accuracy`
 * values are seen. 50m ≈ "directions still land you on the right block."
 */
export const DIRECTIONS_ACCURACY_THRESHOLD_M = 50;

/** Whether the coordinate is precise enough to offer routing (see threshold). */
export function isRoutableAccuracy(accuracy: number): boolean {
  return accuracy <= DIRECTIONS_ACCURACY_THRESHOLD_M;
}

/**
 * Build a directions URL routing TO the scan coordinate.
 *
 * `origin` (the viewer's own location) is only NEEDED on web: OpenStreetMap
 * won't guess a starting point from a link, so desktop routing requires the
 * viewer's coordinate (obtained client-side via the geolocation grant — see
 * requestViewerLocation — and used ONLY to build this URL, never stored or sent
 * to MIDEO). On NATIVE, `origin` is omitted: Apple/Google Maps fill the origin
 * from the device itself (their own in-app location permission), so we don't
 * trigger a browser geolocation prompt at all.
 *
 * - android → Google Maps directions (destination only; app supplies origin).
 * - ios     → Apple Maps `daddr` drive directions (app supplies origin).
 * - web     → OpenStreetMap.org `?route=from;to` — this DOES route (unlike the
 *   destination-only `?to=`, which just drops a flag). Keeps routing on OSM, so
 *   the patient coordinate never reaches Google's ad graph. Requires `origin`.
 */
export function buildDirectionsUrl(
  platform: HandoffPlatform,
  destination: { lat: number; lng: number },
  origin?: { lat: number; lng: number },
): string {
  const to = `${destination.lat},${destination.lng}`;

  switch (platform) {
    case 'android':
      return `https://www.google.com/maps/dir/?api=1&destination=${to}`;
    case 'ios':
      return `https://maps.apple.com/?daddr=${to}&dirflg=d`;
    case 'web':
    default: {
      // OSM needs an explicit origin to route; route= is "from;to" (; encoded).
      // Without an origin we can't route on OSM — caller guarantees one on web.
      const from = origin ? `${origin.lat},${origin.lng}` : '';
      return `https://www.openstreetmap.org/directions?route=${from}%3B${to}`;
    }
  }
}

/**
 * Build the platform-appropriate "view the scan location" URL for a coordinate
 * (pin/area view, never routing):
 * - android → `geo:` intent with a `q=LAT,LNG(label)`; the label rides with the
 *   pin on Android, carrying the "approximate ±<accuracy>m" estimate.
 * - ios     → Apple Maps `ll=` (position). We deliberately do NOT pass `q=`:
 *   Apple Maps treats `q=` as a search query and can snap the pin to a nearby
 *   *named place*, displacing it and reading as a confirmed address.
 * - web     → OpenStreetMap.org `mlat/mlon` red marker.
 *
 * OpenStreetMap.org is the desktop target (over Google Maps) for privacy on the
 * PHI-boundary crossing + provenance-consistency with our self-hosted OSM tiles.
 */
export function buildHandoffUrl(platform: HandoffPlatform, location: HandoffLocation): string {
  const { lat, lng, accuracy } = location;

  switch (platform) {
    case 'android':
      // geo:LAT,LNG?q=LAT,LNG(label) — the q=...() label titles the pin.
      return `geo:${lat},${lng}?q=${lat},${lng}(${encodeURIComponent(accuracyLabel(accuracy))})`;
    case 'ios':
      // ll= keeps the exact position; q= omitted to avoid pin displacement.
      return `https://maps.apple.com/?ll=${lat},${lng}`;
    case 'web':
    default:
      // OpenStreetMap.org red marker; zoom 16 ≈ neighborhood level.
      return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`;
  }
}
